import PresetDetailsEditor from '../../../components/PresetDetailsEditor';
import { calculateTextWidth } from '../../../utils/presetLayout';
import { LAYOUT_KEYS, STYLE_KEYS, pick, resolveBoxPreset, resolveRenderableBox, versionBox } from '../../../utils/privateLayouts';

export default function BoxLayoutInspector({ item, presets, defaultId, canvasSize, languages, onChange, onSave, onRemove, onToggleVisibility, saving }) {
  const box = versionBox(item);
  const effective = resolveBoxPreset(box, presets, defaultId);
  const rendered = resolveRenderableBox(box, presets, defaultId, canvasSize, languages).box;
  const changeConfig = (value) => {
    const next = typeof value === 'function' ? value(effective) : value;
    const config = next.config || {};
    const styleOverrides = { ...box.styleOverrides };
    STYLE_KEYS.forEach((key) => {
      if (JSON.stringify(config[key]) !== JSON.stringify(effective.config[key])) styleOverrides[key] = config[key];
    });
    const layoutConfig = { ...box.layoutConfig };
    LAYOUT_KEYS.forEach((key) => {
      if (JSON.stringify(config[key]) !== JSON.stringify(effective.config[key])) layoutConfig[key] = config[key];
    });
    let position = box.position;
    if (LAYOUT_KEYS.some((key) => key.startsWith('textWidth') && config[key] !== effective.config[key]) && rendered.position) {
      const padding = typeof config.padding === 'number' ? config.padding : config.padding?.left || 0;
      position = { ...rendered.position, width: calculateTextWidth({ config }, canvasSize) + padding * 2 };
    }
    onChange({ ...box, styleOverrides, layoutConfig, position });
  };
  const move = (key, value) => onChange({ ...box, position: { ...rendered.position, [key]: Number(value) },
    layoutConfig: { ...box.layoutConfig, positionMode: 'custom' } });
  return <div className="space-y-4">
    <h2 className="font-semibold">{box.role === 'main' ? 'Main text' : box.name || 'Text box'}</h2>
    <label className="block text-sm">Text style
      <select className="mt-1 w-full rounded border p-2" value={box.stylePresetId || ''}
        onChange={(event) => onChange({ ...box, stylePresetId: event.target.value || null })}>
        <option value="">Use book default</option>
        {presets.map((preset) => <option key={preset._id} value={preset._id}>{preset.name}</option>)}
      </select>
    </label>
    <p className="text-xs text-slate-500">Effective style: {effective.name}</p>
    <button className="text-xs text-blue-700" type="button" onClick={() => onChange({ ...box, styleOverrides: {} })}>Reset style overrides</button>
    <label className="block text-sm">Placement
      <select className="mt-1 w-full rounded border p-2" value={box.layoutConfig?.positionMode === 'automatic' ? 'automatic' : 'custom'}
        onChange={(event) => onChange({ ...box, position: rendered.position, layoutConfig: { ...box.layoutConfig, positionMode: event.target.value } })}>
        <option value="custom">Fixed position and bounds</option><option value="automatic">Automatic centering</option>
      </select>
    </label>
    {box.layoutConfig?.positionMode !== 'automatic' && <div className="grid grid-cols-2 gap-2">
      {['x', 'y', 'width', 'height'].map((key) => <label key={key} className="text-xs capitalize">{key}
        <input type="number" min={['width', 'height'].includes(key) ? 1 : undefined} value={Math.round(rendered.position?.[key] || 0)}
          onChange={(event) => move(key, event.target.value)} className="w-full rounded border p-2" />
      </label>)}
    </div>}
    <PresetDetailsEditor selectedPreset={{ ...effective, config: { ...effective.config, ...pick(box.layoutConfig, LAYOUT_KEYS) } }}
      setSelectedPreset={changeConfig} pageLanguage={languages.join('_')} title="Box formatting" subtitle="Saved only on this page"
      showActions={false} showSaveAsNew={false} showDiscardChanges={false} allowTextVisibility
      textVisibilityOverride={box.layoutConfig?.textVisibility || 'both'} />
    <button type="button" disabled={saving} onClick={onSave} className="w-full rounded bg-slate-900 p-2 text-sm text-white">{saving ? 'Saving…' : 'Save text box'}</button>
    <button type="button" disabled={saving} onClick={onToggleVisibility} className="w-full rounded border border-slate-300 p-2 text-sm text-slate-700">{box.visibleOnCanvas === false ? 'Show on canvas' : 'Hide from canvas'}</button>
    <p className="text-xs text-slate-500">Hiding keeps this box’s position and formatting. Save the layout or text box to keep the change.</p>
    <button type="button" disabled={saving} onClick={onRemove} className="w-full rounded border border-red-200 p-2 text-sm text-red-700">Remove from layout</button>
    <p className="text-xs text-slate-500">Wording and translations stay below the editor. Use Add to layout to place them again.</p>
  </div>;
}
